import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Button from "../../../../Components/Utils/Button/Button";
import Form from "../../../../Components/Utils/Form/Form";
import FloatingLabelInput from "../../../../Components/Utils/Form/FloatingLabelInput/FloatingLabelInput";
import FloatingLabelSelect from "../../../../Components/Utils/Form/FloatingLabelSelect/FloatingLabelSelect";
import CreateImprovementQuestionSection from "../../../../Components/CreateImprovement/CreateImprovementQuestionSection/CreateImprovementQuestionSection";
import { FloatingLabelSelectOption } from "../../../../Types/Form/FloatingLabelSelectOption";
import { RootState, useAppSelector } from "../../../../Redux/Store/Store";
import { getClient } from "../../../../Redux/Store/Slices/client";
import {
  CreateDevelopImprovement as createDevelopImprovementApi,
  CreateEvaluateImprovement as createEvaluateImprovementApi,
  GetImprovementPlanDisplayInfoByClientAndUser,
} from "../../../../API/Improve/Improve";
import { toast } from "react-toastify";
import { CreateDevelopImprovementRequest } from "../../../../Types/Develop/CreateDevelopImprovementRequest";
import { CreateEvaluateImprovementRequest } from "../../../../Types/Evaluate/CreateEvaluateImprovementRequest";
import { ProductType } from "../../../../Types/ProductType";
import styles from "./CreateDevelopImprovement.module.css";

type ImprovementFormData = {
  title: string;
  improvementPlanId: string;
  areaId: string;
  questionGroups: { questionGroupId: string; selectedQuestions: string[] }[];
};

const CreateDevelopImprovement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { areaId } = useParams<{ areaId: string }>();

  const reduxAuth = useSelector((state: RootState) => state.auth);
  const reduxQ = useSelector((state: RootState) => state.questionnaire);
  const client = useAppSelector(getClient);

  const isEvaluate = reduxQ.questionnaire?.productType === ProductType.Evaluate;
  const productPath = isEvaluate ? "evaluate" : "develop";

  const [improvementPlansOptions, setImprovementPlansOptions] = useState<FloatingLabelSelectOption[]>([]);
  const [improvementData, setImprovementData] = useState<ImprovementFormData>({
    title: "",
    improvementPlanId: "",
    areaId: areaId ?? "",
    questionGroups: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const defaultSectionId = useMemo(() => new URLSearchParams(location.search).get("sectionId") ?? "", [location.search]);
  const defaultCriteriaId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("criteriaId") ?? params.get("itemId") ?? "";
  }, [location.search]);

  const canSubmit = useMemo(() => {
    return Boolean(areaId)
      && improvementData.title.trim().length > 0
      && improvementData.improvementPlanId.trim().length > 0
      && (improvementData.questionGroups?.length ?? 0) > 0;
  }, [areaId, improvementData]);

  const fullCriteriaSelection = useMemo(() => {
    const sections = reduxQ.questionnaire?.questionnaireSections ?? [];
    return sections
      .filter((s) => (s.criteria?.length ?? 0) > 0)
      .map((s) => ({
        questionGroupId: s.id,
        selectedQuestions: s.criteria!.map((c) => c.id),
      }));
  }, [reduxQ.questionnaire?.questionnaireSections]);

  const allCriteriaSelectedGlobally = useMemo(() => {
    if (fullCriteriaSelection.length === 0) return false;
    const groups = improvementData.questionGroups ?? [];
    if (groups.length !== fullCriteriaSelection.length) return false;
    return fullCriteriaSelection.every((expected) => {
      const g = groups.find((x) => x.questionGroupId === expected.questionGroupId);
      if (!g) return false;
      if (g.selectedQuestions.length !== expected.selectedQuestions.length) return false;
      return expected.selectedQuestions.every((id) => g.selectedQuestions.includes(id));
    });
  }, [improvementData.questionGroups, fullCriteriaSelection]);

  const toggleSelectAllCriteriaGlobally = () => {
    if (fullCriteriaSelection.length === 0) return;
    setImprovementData((prev) => ({
      ...prev,
      questionGroups: allCriteriaSelectedGlobally ? [] : fullCriteriaSelection,
    }));
  };

  useEffect(() => {
    if (!areaId) return;
    setImprovementData((prev) => ({ ...prev, areaId }));
  }, [areaId]);

  useEffect(() => {
    if (!defaultSectionId || !defaultCriteriaId) return;

    setImprovementData((prev) => {
      const existingGroups = prev.questionGroups ?? [];
      const existingGroup = existingGroups.find((group) => group.questionGroupId === defaultSectionId);

      if (existingGroup?.selectedQuestions.includes(defaultCriteriaId)) {
        return prev;
      }

      const nextGroups = existingGroup
        ? existingGroups.map((group) =>
            group.questionGroupId === defaultSectionId
              ? {
                  ...group,
                  selectedQuestions: [...group.selectedQuestions, defaultCriteriaId],
                }
              : group,
          )
        : [
            ...existingGroups,
            {
              questionGroupId: defaultSectionId,
              selectedQuestions: [defaultCriteriaId],
            },
          ];

      return {
        ...prev,
        questionGroups: nextGroups,
      };
    });
  }, [defaultSectionId, defaultCriteriaId]);

  useEffect(() => {
    const fetchImprovementPlanData = async () => {
      if (!client?.id || client.id === "undefined" || client.id === "null" || !(reduxAuth.user?.sub || reduxAuth.user?.id)) {
        return;
      }

      try {
        const res = await GetImprovementPlanDisplayInfoByClientAndUser(
          client.id,
          reduxAuth.user.sub || reduxAuth.user.id,
          isEvaluate ? ProductType.Evaluate : ProductType.Develop,
          !isEvaluate ? areaId : undefined,
        );
        const options: FloatingLabelSelectOption[] = res.map((item: any) => ({
          value: item.id ?? item.Id,
          label: item.title ?? item.Title,
        }));
        setImprovementPlansOptions(options);
      } catch (error) {
        console.error("Failed to fetch improvement plans:", error);
        toast.error("Failed to load improvement plans");
      }
    };

    fetchImprovementPlanData();
  }, [reduxAuth.user?.id, reduxAuth.user?.sub, client?.id, isEvaluate, areaId]);

  const handleSubmit = async () => {
    if (!canSubmit || !areaId) return;
    setSubmitting(true);
    try {
      if (isEvaluate) {
        const request: CreateEvaluateImprovementRequest = {
          title: improvementData.title,
          improvementPlanId: improvementData.improvementPlanId,
          evaluateAreaId: areaId,
          questionGroups: improvementData.questionGroups,
        };
        await createEvaluateImprovementApi(request);
      } else {
        const request: CreateDevelopImprovementRequest = {
          title: improvementData.title,
          improvementPlanId: improvementData.improvementPlanId,
          developAreaId: areaId,
          questionGroups: improvementData.questionGroups,
        };
        await createDevelopImprovementApi(request);
      }

      toast.success("Objective created");

      setImprovementData({
        title: "",
        improvementPlanId: "",
        areaId,
        questionGroups: [],
      });

      if (isEvaluate) {
        // Evaluate: stay on the page (form is already reset above)
      } else {
        const activeSectionId = reduxQ.activeSectionId;
        const activeCriteriaId = reduxQ.activeCriteriaId;
        if (activeSectionId && activeCriteriaId) {
          navigate(`/${productPath}/${areaId}/${activeSectionId}/${activeCriteriaId}`);
        } else {
          navigate(`/${productPath}/${areaId}`);
        }
      }
    } catch (error) {
      console.error("Error creating improvement:", error);
      toast.error("Failed to create improvement. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <h1 className={styles.title}>Create Improvement</h1>
        <p className={styles.subtitle}>
          Create an improvement in the Track module, then link it to one or more criteria.
        </p>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div>
            <p className={styles.cardTitle}>Improvement Setup</p>
            <p className={styles.cardHint}>Define the improvement title and select where it should be created.</p>
          </div>
        </div>
        <Form>
          <FloatingLabelInput
            placeholder="Add Improvement Title..."
            label="Improvement title"
            required
            value={improvementData.title}
            onChange={(e) =>
              setImprovementData((prev) => ({
                ...prev,
                title: e.target.value,
              }))
            }
          />
          <FloatingLabelSelect
            label="Improvement plan"
            options={improvementPlansOptions}
            required
            value={improvementData.improvementPlanId}
            openMessage={
              !isEvaluate
                ? "Showing only plans linked to this Develop user."
                : undefined
            }
            onChange={(e) =>
              setImprovementData((prev) => ({
                ...prev,
                improvementPlanId: e.target.value,
              }))
            }
          />
        </Form>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div>
            <p className={styles.cardTitle}>Link Criteria</p>
            <p className={styles.cardHint}>Select one or more criteria to link this improvement to.</p>
          </div>
          <div className={styles.linkCriteriaHeaderActions}>
            {fullCriteriaSelection.length > 0 && (
              <button
                type="button"
                className={styles.globalSelectAll}
                onClick={toggleSelectAllCriteriaGlobally}
              >
                {allCriteriaSelectedGlobally ? "Clear all" : "Select all"}
              </button>
            )}
            <span className={styles.selectionCount}>
              {(improvementData.questionGroups ?? []).reduce((acc, group) => acc + group.selectedQuestions.length, 0)} selected
            </span>
          </div>
        </div>
        <div className={styles.criteriaGrid}>
          {reduxQ.questionnaire?.questionnaireSections.map((section) => {
            const selectedQuestionIds =
              improvementData.questionGroups?.find((group) => group.questionGroupId === section.id)?.selectedQuestions ?? [];

            return (
              <CreateImprovementQuestionSection
                key={section.id}
                title={section.title}
                id={section.id}
                criteria={section.criteria}
                openOnLoad={section.id === defaultSectionId}
                improvementData={improvementData}
                setImprovementData={setImprovementData}
                selectedQuestionIds={selectedQuestionIds}
              />
            );
          })}
        </div>
      </div>

      <div className={styles.actionBar}>
        <span className={styles.submitCaption}>Tip: use the "Link Improvement" button within a criteria to expedite the linking process.</span>
        <Button clickHandler={handleSubmit} disabled={!canSubmit || submitting} autoDisable>
          Create Improvement
        </Button>
      </div>
    </div>
  );
};

export default CreateDevelopImprovement;

